<template>
  <div v-if="isLoading">
    <BitcoinDetailBlocksSkeleton />
  </div>
  <div v-else class="bitcoin-detail-blocks">
    <div class="bitcoin-detail-blocks_title">
      <Text
        pattern="body2"
        class="bitcoin-detail-blocks_title-text"
        :weight="600"
        color="#787878"
      >
        {{ $t("Inputs") }} / {{ $t("Outputs") }}
      </Text>
    </div>
    <div class="bitcoin-detail-blocks_content">
      <div class="bitcoin-detail-blocks_header">
        <div v-if="isCoinbase" class="coinbase-card">
          <BaseSvgIcon name="coinbase-logo" />
          Coinbase
        </div>
        <template v-else>
          <!-- Use the new AddressBlock component -->
          <AddressBlock
            v-for="(input, index) in displayInputs"
            :key="`input-${index}`"
            :title="`${$t('Input')} ${index + 1}`"
            :address="input.address"
            :network="network"
            :amount="input.amount"
            :symbol="input.symbol"
            :formattedFiat="input.formattedFiat"
            :amountTransferred="input"
            type="input"
          />
        </template>
      </div>
      <div class="bitcoin-detail-blocks_column">
        <!-- Use the new AddressBlock component -->
        <AddressBlock
          v-for="(output, index) in displayOutputs"
          :key="`output-${index}`"
          :title="`${$t('Output')} ${index + 1}`"
          :address="output.address"
          :amount="output.amount"
          :symbol="output.symbol"
          :network="network"
          :formattedFiat="output.formattedFiat"
          :amountTransferred="output"
          direction="out"
          type="output"
        />
      </div>
    </div>
    <button
      v-if="inputs.length > 5 || outputs.length > 5"
      class="show-more"
      @click="toggleShowAll"
    >
      {{
        $t(
          showAll
            ? "PaginationButton | ShowLess"
            : "PaginationButton | ShowMore",
        )
      }}
      <BaseSvgIcon
        name="arrow-right-simple"
        class="arrow"
        :class="{ 'rotate-up': showAll }"
      />
    </button>
  </div>
</template>

<script>
import { Text } from "@/shared/ui";
// Ensure correct import path for AddressBlock
import AddressBlock from "@/features/TransactionDetails/Common/ui/AddressBlock.vue";
import BaseSvgIcon from "@/shared/ui/BaseSvgIcon/BaseSvgIcon.vue";
import "@/assets/icons/SVG/arrow-right-simple.svg";
import "@/assets/icons/SVG/coinbase-logo.svg";
import BitcoinDetailBlocksSkeleton from "./BitcoinDetailBlocksSkeleton.vue";

export default {
  name: "BitcoinDetailBlocks", // Renamed component
  components: {
    Text,
    AddressBlock, // Use new component
    BaseSvgIcon,
    BitcoinDetailBlocksSkeleton,
  },
  data() {
    return {
      showAll: false,
    };
  },
  props: {
    inputs: {
      type: Array,
      required: true,
      default: () => [],
    },
    outputs: {
      type: Array,
      required: true,
      default: () => [],
    },
    network: {
      type: String,
      required: true,
    },
    isCoinbase: {
      type: Boolean,
      default: false,
    },
    totalInput: {
      type: Object,
      default: null,
    },
    totalOutput: {
      type: Object,
      default: null,
    },
    fee: {
      type: Object,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    displayInputs() {
      return this.showAll ? this.inputs : this.inputs.slice(0, 5);
    },
    displayOutputs() {
      return this.showAll ? this.outputs : this.outputs.slice(0, 5);
    },
  },
  methods: {
    toggleShowAll() {
      this.showAll = !this.showAll;
    },
  },
};
</script>

<style lang="scss" scoped>
/* Renamed main class and restored original styles */
.bitcoin-detail-blocks {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  border-radius: 10px;
  border: 1px solid var(--Dark-850, #1a1a1a);
  background: var(--Dark-900, #111);
  padding: 32px;

  &_title {
    padding-bottom: 10px;
    margin-bottom: 16px;
    border-bottom: 1px solid var(--Dark-850, #1a1a1a);

    &-text {
      font-size: 16px !important;
      color: #fafafa !important;
      line-height: 150% !important;
    }
  }

  &_content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    border-bottom: 1px solid var(--Dark-850, #1a1a1a);
    padding-bottom: 24px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  &_column {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &_header {
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media (max-width: 768px) {
      gap: 10px;
    }
  }

  .show-more {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 7px;
    padding-top: 24px;
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    font-size: inherit; // Ensure text size matches surrounding text
  }

  .arrow {
    transition: transform 0.3s ease;
    transform: rotate(90deg);
    width: 16px;
    height: 16px;

    &.rotate-up {
      transform: rotate(-90deg);
    }
  }

  .coinbase-card {
    display: flex;
    align-items: center;
    gap: 13px;
    border-radius: 10px;
    border: 1px solid var(--Dark-850, #1a1a1a);
    padding: 27px 24px; // Размерность как у блоков output
  }
}
</style>
